import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ComponentMirror,
    ComponentRef,
    ElementRef,
    EnvironmentInjector,
    InjectionToken,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    Provider,
    Type,
    ViewContainerRef,
    inject,
    reflectComponentType,
    viewChild,
} from '@angular/core';

import { IConstant } from '@frontend/oxygen/shared-utils';

import { type DefaultFreeBetLabelComponent } from './default.free-bet-label.component';

export const FreeBetLabelComponentToken = new InjectionToken<DefaultFreeBetLabelComponent & Type<any>>('FreeBetLabelComponentToken');

export function provideFreeBetLabelComponent(cmp: typeof DefaultFreeBetLabelComponent): Provider {
    return {
        provide: FreeBetLabelComponentToken,
        useValue: cmp,
    } as Provider;
}

@Component({
    selector: 'free-bet-label',
    template: '<ng-container #baseRef></ng-container>',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class FreeBetLabelComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() plusPrefix: boolean;
    @Input() plusSuffix: boolean;
    @Input() margin: boolean;
    @Input() jump: string;
    @Input() freeBetLabelText: string;
    @Input() selected: string;

    private injector = inject(Injector);
    private envInjector = inject(EnvironmentInjector);
    private readonly vcr = viewChild.required('baseRef', { read: ViewContainerRef });
    private cmpToken = inject(FreeBetLabelComponentToken);

    private cmpRef?: ComponentRef<any>;

    private compMetaData: ComponentMirror<any>;

    constructor(private elem: ElementRef) {
        this.compMetaData = this.cmpToken && reflectComponentType(this.cmpToken)!;
    }

    ngOnChanges() {
        this.syncInputs();
    }

    ngAfterViewInit() {
        if (!this.cmpToken) {
            throw new Error('Please provide a freebet label component!');
        }

        this.cmpRef = this.vcr().createComponent(this.cmpToken, {
            injector: this.injector,
            environmentInjector: this.envInjector,
        });

        this.syncInputs();
    }

    private syncInputs() {
        if (this.cmpRef && this.compMetaData?.inputs) {
            this.compMetaData.inputs.forEach((input: IConstant) => {
                this.cmpRef?.setInput(input.propName, this[input.propName as keyof FreeBetLabelComponent]);
            });

            const dataCrlat = this.elem?.nativeElement?.getAttribute('data-crlat') || this.elem?.nativeElement?.getAttribute('host-data-crlat') || '';
            const hostClasses = this.elem?.nativeElement?.className || this.elem?.nativeElement?.getAttribute('host-classes') || '';
            this.elem.nativeElement.setAttribute('host-classes', hostClasses);
            this.elem.nativeElement.setAttribute('host-data-crlat', dataCrlat);
            this.cmpRef.setInput('dataCrlat', dataCrlat);
            this.cmpRef.setInput('hostClasses', hostClasses);

            this.elem.nativeElement.removeAttribute('class');
            this.elem.nativeElement.removeAttribute('data-crlat');
        }
    }

    ngOnDestroy(): void {
        this.cmpRef?.destroy();
    }
}
